import { isSome } from '@mbfab/shared'
import { sum } from 'es-toolkit'
import { z } from 'zod'

import type { Item } from './millsheet-list-svg'

//
// v1 apps/frontend/client/src/components/MillsheetPrintForm/MillsheetListSvgV2/MainRow.tsx の移植。
// このファイルは antd に依存しない純粋な SVG コンポーネントのため、ロジックはほぼそのまま持ち込んでいる。
// 汎用ミルシート印刷（既存の一覧表）向けの列定義・並び替え定数（REORDERABLE_COLUMN_TYPES 等）は
// 発注照合の集計表では使わない（発注照合専用の列集合は print-dialog.tsx 側で定義する）ため移植していない。
//
export const columnConfigSchema = z.object({
  type: z.union([
    z.literal('steelType'),
    z.literal('sectionType'),
    z.literal('size'),
    z.literal('thicknessDiameter'),
    z.literal('length'),
    z.literal('quantity'),
    z.literal('weight'),
    z.literal('heatNumber'),
    z.literal('individualNumber'),
    z.literal('no'),
    z.literal('manufacturerName'),
    z.literal('constructionArea'),
    z.literal('member'),
    z.literal('buildingPart'),
    z.literal('memo'),
    z.literal('pageNumber'),
    z.literal('tradingCompany'),
    z.literal('orderNumber'),
    z.literal('innerNo'),
    // 発注照合の「鋼材ミルシート集計表」専用の列
    z.literal('endorsementCompany'),
    // ホームの Excel（使用鋼材一覧表）専用の列
    z.literal('endorsementDate'),
    z.literal('customer'),
    z.literal('referenceNumber'),
    z.literal('supplierName'),
  ]),
  active: z.boolean(),
  // 並び替えできる項目の並び方向。省略時は昇順（方向を持たない保存済みの設定をそのまま読めるようにするため）
  direction: z.enum(['asc', 'desc']).optional(),
})
export type ColumnConfig = z.infer<typeof columnConfigSchema>

export const columnTypeWeight: Record<ColumnConfig['type'], number> = {
  steelType: 1.13,
  sectionType: 1,
  size: 1,
  thicknessDiameter: 1,
  length: 1,
  quantity: 0.8,
  weight: 1,
  heatNumber: 1.2,
  individualNumber: 1.2,
  no: 1.2,
  manufacturerName: 1.8,
  constructionArea: 1,
  member: 1,
  buildingPart: 1,
  memo: 1.5,
  pageNumber: 1,
  tradingCompany: 1.2,
  orderNumber: 1.2,
  innerNo: 1.2,
  endorsementCompany: 1.4,
  endorsementDate: 1.2,
  customer: 1.4,
  referenceNumber: 1.4,
  supplierName: 1.4,
}

export const columnTypeLabel: Record<ColumnConfig['type'], string> = {
  steelType: '規格',
  sectionType: '形状',
  size: '寸法',
  thicknessDiameter: '厚・径',
  length: '長さ',
  quantity: '数量',
  weight: '重量',
  heatNumber: '製鋼番号',
  individualNumber: '個体番号',
  no: '証明書番号',
  manufacturerName: '製造会社名',
  constructionArea: '工区',
  member: '節',
  buildingPart: '部位',
  memo: '備考',
  pageNumber: 'ページ番号',
  tradingCompany: '商社',
  orderNumber: '発注番号',
  innerNo: '内部番号',
  endorsementCompany: '裏書会社名',
  endorsementDate: '裏書日',
  customer: '需要家名',
  referenceNumber: '参照番号',
  supplierName: 'サプライヤー名',
}

export function getXForColumns(columnConfigs: ColumnConfig[], width: number, offset: number) {
  const weights = columnConfigs.map((columnConfig) => columnTypeWeight[columnConfig.type])
  const columnWidthBase = width / sum(weights)
  return columnConfigs.reduce(
    (acc, columnConfig) => {
      const prev = acc.length > 0 ? acc[acc.length - 1]!.columnRight : offset
      const columnWidth = columnWidthBase * columnTypeWeight[columnConfig.type]
      return [
        ...acc,
        {
          columnConfig,
          columnCenter: columnWidth / 2 + prev + 2, // どこかの設定がずれており、2px分のオフセットが必要。borderの分だろうか？
          columnRight: columnWidth + prev,
        },
      ]
    },
    [] as Array<{ columnRight: number; columnCenter: number; columnConfig: ColumnConfig }>,
  )
}

export function MainRow({
  item,
  index,
  indexStart,
  columnConfigs,
}: {
  item: Item
  index: number
  indexStart: number
  columnConfigs: ColumnConfig[]
}) {
  const y = 490 + index * 45
  const textY = y + 29
  const xForColumns = getXForColumns(columnConfigs, 860, 95)

  return (
    <g id="row">
      <rect id="columns-header_3" x={95} y={y} width={860} height={45} stroke="black" strokeWidth={2} />
      {xForColumns.map(({ columnRight }, index) => {
        return <line key={index} x1={columnRight} y1={y} x2={columnRight} y2={y + 45} stroke="black" strokeWidth={2} />
      })}
      {xForColumns.map(({ columnCenter, columnConfig }, index) => {
        return (
          <text
            key={index}
            data-testid={
              columnConfig.type === 'pageNumber' && isSome(item[columnConfig.type])
                ? 'print-summary-page-number'
                : undefined
            }
            data-page-number={columnConfig.type === 'pageNumber' ? item[columnConfig.type] : undefined}
            fill="black"
            xmlSpace="preserve"
            style={{
              whiteSpace: 'pre',
            }}
            fontFamily="Hiragino Kaku Gothic"
            fontSize={autoResizeFontSize(item[columnConfig.type] ?? '')}
            textAnchor="middle"
          >
            <tspan x={columnCenter} y={textY}>
              {columnConfig.type === 'weight' || columnConfig.type === 'quantity'
                ? numberWithDelimiterFilter(item[columnConfig.type])
                : item[columnConfig.type]}
            </tspan>
          </text>
        )
      })}
    </g>
  )
}

function numberWithDelimiterFilter(x: unknown): string {
  if (typeof x === 'string' || typeof x === 'number') {
    const str = x.toString()
    const [integer, decimal] = str.split('.')
    const intWithDelimiter = integer?.replace(/(\d)(?=(\d{3})+$)/g, '$1,') ?? '0'
    return isSome(decimal) ? `${intWithDelimiter}.${decimal}` : intWithDelimiter
  }
  return ''
}

function autoResizeFontSize(text: string | number) {
  if (typeof text !== 'string') {
    return 15
  }
  if (text.length > 19) {
    return 7
  }
  if (text.length > 16) {
    return 9
  }
  if (text.length > 13) {
    return 11
  }
  if (text.length > 11) {
    return 13
  }
  if (text.length > 9) {
    return 14
  }
  return 15
}
